import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, Phone, Mail, CheckCircle2 } from "lucide-react";
import api, { formatErr, fmtDay } from "../../lib/api";
import { Loading, PageHeader } from "../../components/Shared";

const PREF_DEFS = [
  { key: "care_updates", label: "Important care updates", desc: "Assessment updates, Care Manager assignment, service updates" },
  { key: "visit_reminders", label: "Visit reminders", desc: "Reminders before scheduled Care Manager visits" },
  { key: "reports", label: "Report notifications", desc: "Visit completed and report ready" },
  { key: "billing", label: "Billing notifications", desc: "Payment and subscription confirmations, receipts" },
];

function Toggle({ on, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} role="switch" aria-checked={on}
      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${on ? "bg-emerald-700" : "bg-stone-300"}`}>
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${on ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  );
}

export default function Communication() {
  const [d, setD] = useState(null);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/family/communication").then((r) => { setD(r.data); setPhone(r.data.phone || ""); }).catch((e) => toast.error(formatErr(e)));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!d || d.wa_connected) return undefined;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/public/wa-status/${d.wa_code}`);
        if (r.data.connected) await load();
      } catch { /* keep polling */ }
    }, 6000);
    return () => clearInterval(t);
  }, [d?.wa_connected, d?.wa_code]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!d) return <Loading />;
  const prefs = d.prefs || {};

  const toggle = async (key) => {
    try {
      const r = await api.put("/family/communication", { prefs: { [key]: !prefs[key] } });
      setD({ ...d, prefs: r.data.prefs });
      toast.success("Preference saved");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const sendOtp = async () => {
    setBusy(true);
    try {
      const r = await api.post("/phone/send-otp", { phone });
      setOtpSent(r.data);
      toast.success("Verification code sent by SMS");
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const verifyOtp = async () => {
    setBusy(true);
    try {
      await api.post("/phone/verify", { code: otp });
      toast.success("Mobile number verified");
      setOtpSent(null); setOtp(""); await load();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="communication-page">
      <PageHeader title="Communication" sub="How ParentOne reaches you — WhatsApp first, SMS fallback, email for receipts and records." />

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="p1-card p-6" data-testid="wa-section">
          <p className="flex items-center gap-2 font-heading text-lg font-semibold text-stone-900"><MessageSquare className="h-5 w-5 text-emerald-800" /> WhatsApp updates</p>
          {d.wa_connected ? (
            <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="wa-connected-state">
              <p className="flex items-center gap-2 font-bold text-emerald-900"><CheckCircle2 className="h-5 w-5" /> WhatsApp Connected ✓</p>
              <p className="mt-1 text-sm text-stone-600">{d.wa_phone} · connected {fmtDay(d.wa_connected_at)}</p>
            </div>
          ) : (
            <div className="mt-4">
              <p className="text-sm text-stone-600">Get assessment updates, Care Manager assignment, visit reminders &amp; reports, service updates, payment confirmations and important care alerts on WhatsApp. Recommended — not mandatory.</p>
              {d.wa_available && d.wa_link ? (
                <a href={d.wa_link} target="_blank" rel="noreferrer" className="p1-btn-primary mt-4 flex justify-center" data-testid="wa-activate-btn">Activate WhatsApp Updates</a>
              ) : (
                <p className="mt-4 rounded-xl bg-stone-50 p-3 text-center text-sm text-stone-700" data-testid="wa-code-display">
                  Send <strong>P1-ACTIVATE {d.wa_code}</strong> to ParentOne&apos;s official WhatsApp Business number.</p>
              )}
              <p className="mt-2 text-center text-xs text-stone-400">Your activation code: <strong data-testid="wa-code">{d.wa_code}</strong> — pre-filled, just press send. We&apos;ll detect it automatically.</p>
            </div>
          )}
          <div className="mt-4 flex items-center justify-between rounded-xl border border-stone-100 p-3" data-testid="wa-optin-row">
            <div>
              <p className="text-sm font-semibold text-stone-800">Send me important service updates on WhatsApp</p>
              <p className="text-xs text-stone-400">Consent for service-request confirmations and status updates{d.whatsapp_opt_in_at ? ` · recorded ${fmtDay(d.whatsapp_opt_in_at)}` : ""}.</p>
            </div>
            <Toggle on={!!d.whatsapp_opt_in} testid="wa-optin-toggle" onClick={async () => {
              try {
                const r = await api.put("/family/communication", { whatsapp_opt_in: !d.whatsapp_opt_in });
                setD({ ...d, whatsapp_opt_in: r.data.whatsapp_opt_in });
                toast.success(r.data.whatsapp_opt_in ? "WhatsApp service updates opted in" : "WhatsApp service updates opted out");
              } catch (e) { toast.error(formatErr(e)); }
            }} />
          </div>
        </div>

        <div className="space-y-6">
          <div className="p1-card p-6" data-testid="mobile-section">
            <p className="flex items-center gap-2 font-heading text-lg font-semibold text-stone-900"><Phone className="h-5 w-5 text-emerald-800" /> Mobile number</p>
            <div className="mt-3 flex items-center gap-2">
              <input className="p1-input flex-1" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98XXXXXXXX" data-testid="mobile-input" />
              {d.phone_verified
                ? <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-800" data-testid="mobile-verified">Verified</span>
                : <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-800" data-testid="mobile-unverified">Not verified</span>}
            </div>
            {!d.phone_verified && (
              <div className="mt-3">
                {!otpSent ? (
                  <button onClick={sendOtp} disabled={busy} className="p1-btn-outline !px-4 !py-2 text-xs" data-testid="send-otp-btn">{busy ? "Sending…" : "Verify mobile (SMS code)"}</button>
                ) : (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input className="p1-input flex-1" value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="6-digit code" data-testid="otp-input" />
                      <button onClick={verifyOtp} disabled={busy} className="p1-btn-primary !px-4 !py-2 text-xs" data-testid="verify-otp-btn">{busy ? "…" : "Verify"}</button>
                    </div>
                    {otpSent.test_mode && otpSent.test_code && (
                      <p className="rounded-lg bg-amber-50 p-2 text-xs text-amber-800" data-testid="otp-test-code">TEST mode — SMS is simulated. Code: <strong>{otpSent.test_code}</strong></p>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="mt-4 flex items-center justify-between border-t border-stone-100 pt-4">
              <div>
                <p className="text-sm font-semibold text-stone-800">SMS fallback</p>
                <p className="text-xs text-stone-500">If WhatsApp isn&apos;t connected or delivery fails, send important alerts by SMS.</p>
              </div>
              <Toggle on={!!prefs.sms_fallback} onClick={() => toggle("sms_fallback")} testid="pref-sms-fallback" />
            </div>
          </div>

          <div className="p1-card p-6" data-testid="email-section">
            <p className="flex items-center gap-2 font-heading text-lg font-semibold text-stone-900"><Mail className="h-5 w-5 text-emerald-800" /> Email</p>
            <div className="mt-3 flex items-center justify-between">
              <p className="text-sm text-stone-700">{d.email}</p>
              {d.email_verified
                ? <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-800" data-testid="email-verified">Verified</span>
                : <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-800" data-testid="email-unverified">Unverified</span>}
            </div>
            <p className="mt-1 text-xs text-stone-400">Used for receipts, reports and non-urgent records.</p>
          </div>
        </div>
      </div>

      <div className="p1-card mt-6 p-6" data-testid="prefs-section">
        <p className="font-heading text-lg font-semibold text-stone-900">Care notification preferences</p>
        <p className="mt-1 text-sm text-stone-500">We only send meaningful updates — never a message for every checklist action or vital entry.</p>
        <div className="mt-4 space-y-3">
          {PREF_DEFS.map((p) => (
            <div key={p.key} className="flex items-center justify-between rounded-xl border border-stone-100 p-3">
              <div>
                <p className="text-sm font-semibold text-stone-800">{p.label}</p>
                <p className="text-xs text-stone-400">{p.desc}</p>
              </div>
              <Toggle on={!!prefs[p.key]} onClick={() => toggle(p.key)} testid={`pref-${p.key}`} />
            </div>
          ))}
          <div className="flex items-center justify-between rounded-xl border border-stone-200 bg-stone-50 p-3">
            <div>
              <p className="text-sm font-semibold text-stone-800">Marketing &amp; offers <span className="text-xs font-normal text-stone-400">(separate from care notifications)</span></p>
              <p className="text-xs text-stone-400">Occasional ParentOne news and plan offers. Off by default.</p>
            </div>
            <Toggle on={!!prefs.marketing} onClick={() => toggle("marketing")} testid="pref-marketing" />
          </div>
        </div>
      </div>
    </div>
  );
}
